import { Component, type ReactNode } from 'react'
import { WarningCircle } from '@phosphor-icons/react'
import { Button } from './Button'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="flex flex-col items-center justify-center min-h-[300px] p-8 text-center">
          <div className="bg-danger/10 p-4 rounded-full mb-4">
            <WarningCircle size={48} weight="light" className="text-danger" />
          </div>
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            Something went wrong
          </h2>
          <p className="text-sm text-text-secondary max-w-md mb-1">
            An unexpected error occurred. Try refreshing the page or going back.
          </p>
          {this.state.error && (
            <p className="text-xs text-text-muted font-mono mt-2 max-w-lg break-all">
              {this.state.error.message}
            </p>
          )}
          <div className="flex gap-3 mt-6">
            <Button variant="secondary" onClick={this.handleReset}>
              Try Again
            </Button>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
